/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Button } from "@farmlands/shadcn-ui";
import { SuccessModal } from "@farmlands/ui-library";
import { Mail, Phone } from "lucide-react";
import { FC, JSX } from "react";

import TestIds from "../../../../../testIds";
import { RelationshipOwner } from "../../../../azure-b2c/storage";

export interface IDeleteAccountSuccessProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  relationshipOwner?: RelationshipOwner;
  relationshipType?: string;
}

const DeleteAccountSuccess: FC<IDeleteAccountSuccessProps> = ({
  open,
  onOpenChange,
  relationshipOwner,
  relationshipType,
}): JSX.Element => (
  <SuccessModal
    open={open}
    onOpenChange={onOpenChange}
    title="Request to delete account received"
    buttonLabel="Close"
    testId={TestIds.feat.account.profile.deleteAccountModal.success.component}
    message={
      <div className="flex flex-col text-left">
        <p
          className="p-2 text-center text-balance"
          data-testid={TestIds.feat.account.profile.deleteAccountModal.success.confirmation}
        >
          Thank you for your request. Your request has been forwarded to our customer services team
          and will be actioned within the next 48 hours. Please note that your Farmlands
          shareholding account will remain active.
        </p>

        <div
          className="mt-6 flex flex-col gap-4 rounded-lg bg-grazing-green-50 p-4"
          data-testid={TestIds.feat.account.profile.deleteAccountModal.contactCard}
        >
          <p>
            Contact your Farmlands representative if you have any questions about your Farmlands
            account and FarmlandsPRO.
          </p>

          <div className="flex flex-col items-start gap-2">
            {relationshipOwner?.fullName && (
              <p className="font-semibold">
                {relationshipOwner.fullName}
                {relationshipType ? `, ${relationshipType}` : ""}
              </p>
            )}

            {relationshipOwner?.phone && (
              <Button asChild variant="link-inline" className="text-2xs">
                <a href={`tel:${relationshipOwner.phone}`}>
                  <Phone />
                  {relationshipOwner.phone}
                </a>
              </Button>
            )}

            {relationshipOwner?.email && (
              <Button asChild variant="link-inline" className="text-2xs">
                <a href={`mailto:${relationshipOwner.email}`}>
                  <Mail />
                  {relationshipOwner.email}
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    }
  />
);

export default DeleteAccountSuccess;
